import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, Lock, UserPlus, Loader2 } from 'lucide-react';
import { api, useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';

const fehler = (e) => toast.error(e?.response?.data?.detail || 'Aktion fehlgeschlagen');
const VERSCHWIEGENHEIT = 'Die schriftliche Verpflichtung zur Verschwiegenheit mit Belehrung über die strafrechtlichen Folgen (§ 203 Abs. 4 StGB, § 43e BRAO) liegt vor.';
const ZUGRIFF = [['alle', 'Alle Akten'], ['zugewiesen', 'Nur zugewiesene Akten'], ['rechtsgebiete', 'Nur bestimmte Rechtsgebiete']];
const feld = 'h-9 px-3 rounded-[10px] border border-gray-200 bg-white text-[13px] focus:outline-none focus:border-[#1B3A5C] focus:ring-2 focus:ring-[#e0b56a]/40';

const Einladen = ({ katalog, onDone }) => {
  const [f, setF] = useState({ name: '', email: '', kanzlei_rolle: 'mitarbeiter', verschwiegenheit_bestaetigt: false });
  const [busy, setBusy] = useState(false);
  const senden = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post('/kanzlei/rechte/team/einladen', f); toast.success('Einladung versendet'); setF({ ...f, name: '', email: '' }); onDone(); }
    catch (err) { fehler(err); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={senden} className="flex flex-wrap items-end gap-2 p-4 bg-[#f9fbfc] border border-gray-200 rounded-2xl" data-testid="team-einladen-form">
      <input required placeholder="Vor- und Nachname" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${feld} flex-1 min-w-[160px]`} data-testid="team-einladen-name" />
      <input required type="email" placeholder="E-Mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${feld} flex-1 min-w-[200px]`} data-testid="team-einladen-email" />
      <select value={f.kanzlei_rolle} onChange={(e) => setF({ ...f, kanzlei_rolle: e.target.value })} className={feld} data-testid="team-einladen-rolle">
        {katalog.rollen.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
      </select>
      <button disabled={busy} className="h-9 px-4 rounded-[10px] bg-[#1B3A5C] hover:bg-[#15304d] text-white text-[13px] inline-flex items-center gap-2 disabled:opacity-60" data-testid="team-einladen-senden">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Einladen
      </button>
      {f.kanzlei_rolle === 'freelancer' && (
        <label className="basis-full flex items-start gap-2 text-xs text-[#1B3A5C]" data-testid="team-einladen-verschwiegenheit">
          <input type="checkbox" checked={f.verschwiegenheit_bestaetigt} onChange={(e) => setF({ ...f, verschwiegenheit_bestaetigt: e.target.checked })} className="mt-0.5 accent-[#b8893d]" /> {VERSCHWIEGENHEIT}
        </label>
      )}
    </form>
  );
};

const Person = ({ u, katalog, gebiete, darf, eigen, onSaved }) => {
  const [offen, setOffen] = useState(false);
  const patch = async (body) => {
    try { await api.patch(`/kanzlei/rechte/team/${u.user_id}`, body); toast.success('Gespeichert'); onSaved(); } catch (e) { fehler(e); }
  };
  const rolleAendern = (r) => {
    const extra = r === 'freelancer' && !u.verschwiegenheit ? { verschwiegenheit_bestaetigt: window.confirm(`${VERSCHWIEGENHEIT}\n\nBestätigen?`) } : {};
    patch({ kanzlei_rolle: r, ...extra });
  };
  const abw = u.rechte_abweichend || {};
  const basis = new Set(katalog.matrix[u.kanzlei_rolle] || []);
  const toggleRecht = (k) => {
    const an = !u.rechte.includes(k);
    const neu = { ...abw };
    if (an === basis.has(k)) delete neu[k]; else neu[k] = an;
    patch({ rechte_abweichend: neu });
  };
  return (
    <li className="p-4 sm:p-5" data-testid={`rechte-person-${u.user_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <p className="text-[#1B3A5C] font-medium flex items-center gap-2">{u.name}
            {u.gesperrt && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> gesperrt</span>}
          </p>
          <p className="text-xs text-gray-500">{u.email}</p>
        </div>
        <span className={`text-[11px] inline-flex items-center gap-1 ${u.zwei_fa_aktiv ? 'text-emerald-700' : 'text-amber-700'}`} data-testid={`rechte-2fa-${u.user_id}`}>
          {u.zwei_fa_aktiv ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />} 2FA {u.zwei_fa_aktiv ? 'aktiv' : 'fehlt'}
        </span>
        <select disabled={!darf || eigen} value={u.kanzlei_rolle} onChange={(e) => rolleAendern(e.target.value)} className={feld} data-testid={`rechte-rolle-${u.user_id}`}>
          {katalog.rollen.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <select disabled={!darf || ['admin', 'freelancer'].includes(u.kanzlei_rolle)} value={u.kanzlei_rolle === 'freelancer' ? 'zugewiesen' : u.aktenzugriff} onChange={(e) => patch({ aktenzugriff: e.target.value })} className={feld} data-testid={`rechte-zugriff-${u.user_id}`}>
          {ZUGRIFF.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button type="button" onClick={() => setOffen((o) => !o)} className="text-[12px] text-[#b8893d] hover:underline" data-testid={`rechte-details-${u.user_id}`}>{offen ? 'Schließen' : 'Einzelrechte'}</button>
        {darf && !eigen && <button type="button" onClick={() => patch({ gesperrt: !u.gesperrt })} className="text-[12px] text-rose-700 hover:underline" data-testid={`rechte-sperren-${u.user_id}`}>{u.gesperrt ? 'Entsperren' : 'Sperren'}</button>}
      </div>
      {u.aktenzugriff === 'rechtsgebiete' && u.kanzlei_rolle !== 'admin' && (
        <div className="flex flex-wrap gap-1 mt-3" data-testid={`rechte-gebiete-${u.user_id}`}>
          {gebiete.map((g) => {
            const on = (u.aktenzugriff_gebiete || []).includes(g.key);
            const next = on ? u.aktenzugriff_gebiete.filter((x) => x !== g.key) : [...(u.aktenzugriff_gebiete || []), g.key];
            return <button key={g.key} type="button" disabled={!darf} onClick={() => patch({ aktenzugriff_gebiete: next })} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]' : 'bg-white text-gray-500 border-gray-200 hover:border-[#b8893d]'}`}>{g.label}</button>;
          })}
        </div>
      )}
      {offen && (
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5" data-testid={`rechte-einzel-${u.user_id}`}>
          {katalog.rechte.map((r) => {
            const an = u.rechte.includes(r.key);
            const abweichend = r.key in abw;
            return (
              <label key={r.key} className={`flex items-center gap-2 text-[12px] px-2.5 py-1.5 rounded-[10px] border ${abweichend ? 'border-[#e0b56a] bg-[#fbf7f0]' : 'border-gray-100'}`}>
                <input type="checkbox" checked={an} disabled={!darf || u.kanzlei_rolle === 'admin'} onChange={() => toggleRecht(r.key)} className="accent-[#1B3A5C]" data-testid={`rechte-einzel-${u.user_id}-${r.key}`} />
                <span className="text-[#1B3A5C]">{r.label}</span>{abweichend && <span className="ml-auto text-[10px] text-[#b8893d]">abweichend</span>}
              </label>
            );
          })}
        </div>
      )}
    </li>
  );
};

export const TeamRechte = ({ katalog }) => {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [gebiete, setGebiete] = useState([]);
  const laden = useCallback(() => api.get('/kanzlei/rechte/team').then((r) => setTeam(r.data)).catch(fehler), []);
  useEffect(() => { laden(); api.get('/admin/rueckrufe/rechtsgebiete').then((r) => setGebiete(r.data.items || [])).catch(() => {}); }, [laden]);
  if (!team) return <Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" />;
  return (
    <div className="space-y-4" data-testid="rechte-team">
      {katalog.darf_verwalten && <Einladen katalog={katalog} onDone={laden} />}
      <ul className="bg-white border border-gray-200 rounded-2xl divide-y divide-gray-100">
        {team.map((u) => <Person key={u.user_id} u={u} katalog={katalog} gebiete={gebiete} darf={katalog.darf_verwalten} eigen={u.user_id === user?.user_id} onSaved={laden} />)}
      </ul>
    </div>
  );
};
